import { _IdGenerator, AriaLivePoliteness } from "@angular/cdk/a11y";
import { Platform } from "@angular/cdk/platform";
import {
    ChangeDetectorRef,
    Component,
    ComponentRef,
    DOCUMENT,
    ElementRef,
    EmbeddedViewRef,
    inject,
    NgZone,
    OnDestroy,
    viewChild,
    ViewEncapsulation
} from "@angular/core";
import { BasePortalOutlet, CdkPortalOutlet, ComponentPortal, PortalModule, TemplatePortal } from "@angular/cdk/portal";
import { Observable, of, Subject } from "rxjs";
import { VdsAlertConfig } from "@cce/design-system/alert-domain";

const ENTER_ANIMATION = "vds-alert-enter";
const EXIT_ANIMATION = "vds-alert-exit";

@Component({
    selector: "vds-alert-container",
    templateUrl: "./alert-container.html",
    styleUrl: "./alert-container.css",
    encapsulation: ViewEncapsulation.None,
    host: {
        role: "alert",
        class: "vds-alert",
        "[class.vds-alert--enter]": "_animationState === 'visible'",
        "[class.vds-alert--exit]": "_animationState === 'hidden'",
        "(animationend)": "onAnimationEnd($event.animationName)",
        "(animationcancel)": "onAnimationEnd($event.animationName)"
    },
    imports: [PortalModule]
})
export class VdsAlertContainerComponent extends BasePortalOutlet implements OnDestroy {
    private ngZone = inject(NgZone);
    private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
    private changeDetectorRef = inject(ChangeDetectorRef);
    private platform = inject(Platform);
    alertConfig = inject(VdsAlertConfig);

    private document = inject(DOCUMENT);
    private _trackedModals = new Set<Element>();

    private _enterFallback: ReturnType<typeof setTimeout> | undefined;
    private _exitFallback: ReturnType<typeof setTimeout> | undefined;

    /** The number of milliseconds to wait before announcing the alert's content. */
    private readonly _announceDelay: number = 150;

    /** The timeout for announcing the snack bar's content. */
    private _announceTimeoutId: any;

    /** Whether the component has been destroyed. */
    private _destroyed = false;

    /** The portal outlet inside of this container into which the alert content will be loaded. */
    _portalOutlet = viewChild.required(CdkPortalOutlet);

    /** Subject for notifying that the alert has announced to screen readers. */
    readonly _onAnnounce: Subject<void> = new Subject();

    /** Subject for notifying that the alert has exited from view. */
    readonly _onExit: Subject<void> = new Subject();

    /** Subject for notifying that the alert has finished entering the view. */
    readonly _onEnter: Subject<void> = new Subject();

    /** The state of the alert animations. */
    _animationState = "void";

    /** aria-live value for the live region. */
    _live: AriaLivePoliteness;

    /**
     * Role of the live region. This is only for Firefox as there is a known issue where Firefox +
     * JAWS does not read out aria-live message.
     */
    _role?: "status" | "alert";

    /** Unique ID of the aria-live element. */
    readonly _liveElementId = inject(_IdGenerator).getId("vds-alert-container-live-");

    constructor() {
        super();

        // Use aria-live rather than a live role like 'alert' or 'status'
        // because NVDA and JAWS have show inconsistent behavior with live roles.
        if (this.alertConfig.politeness === "assertive" && !this.alertConfig.announcementMessage) {
            this._live = "assertive";
        } else if (this.alertConfig.politeness === "off") {
            this._live = "off";
        } else {
            this._live = "polite";
        }

        // Only set role for Firefox. Set role based on aria-live because setting role="alert" implies
        // aria-live="assertive" which may cause issues if aria-live is set to "polite" above.
        if (this.platform.FIREFOX) {
            if (this._live === "polite") {
                this._role = "status";
            }
            if (this._live === "assertive") {
                this._role = "alert";
            }
        }
    }

    /** Attach a component portal as content to this alert container. */
    attachComponentPortal<T>(portal: ComponentPortal<T>): ComponentRef<T> {
        this._assertNotAttached();
        const result = this._portalOutlet().attachComponentPortal(portal);
        this._afterPortalAttached();
        return result;
    }

    /** Attach a template portal as content to this alert container. */
    attachTemplatePortal<C>(portal: TemplatePortal<C>): EmbeddedViewRef<C> {
        this._assertNotAttached();
        const result = this._portalOutlet().attachTemplatePortal(portal);
        this._afterPortalAttached();
        return result;
    }

    /** Handle end of animations, updating the state of the snackbar. */
    onAnimationEnd(animationName: string): void {
        if (animationName === EXIT_ANIMATION) {
            this._completeExit();
        } else if (animationName === ENTER_ANIMATION) {
            clearTimeout(this._enterFallback);
            this.ngZone.run(() => {
                this._onEnter.next();
                this._onEnter.complete();
            });
        }
    }

    /** Begin animation of alert entrance into view. */
    enter(): void {
        if (!this._destroyed) {
            this._animationState = "visible";
            this.changeDetectorRef.markForCheck();
            this.changeDetectorRef.detectChanges();

            clearTimeout(this._enterFallback);
            this._enterFallback = setTimeout(() => {
                this.elementRef.nativeElement.classList.add("vds-alert--fallback-visible");
                this.onAnimationEnd(ENTER_ANIMATION);
            }, 200);

            this._screenReaderAnnounce();
        }
    }

    /** Begin animation of the alert exiting from view. */
    exit(): Observable<void> {
        if (this._destroyed) {
            return of(undefined);
        }

        this.ngZone.run(() => {
            this._animationState = "hidden";
            this.changeDetectorRef.markForCheck();

            clearTimeout(this._exitFallback);
            this._exitFallback = setTimeout(() => this.onAnimationEnd(EXIT_ANIMATION), 200);
        });

        return this._onExit;
    }

    /** Makes sure the exit callbacks have been invoked when the element is destroyed. */
    ngOnDestroy(): void {
        this._destroyed = true;
        this._clearFromModals();
        this._completeExit();
    }

    /**
     * Waits for the zone to settle before removing the element. Helps prevent
     * errors where we end up removing an element which is in the middle of an animation.
     */
    private _completeExit(): void {
        clearTimeout(this._exitFallback);
        queueMicrotask(() => {
            this._onExit.next();
            this._onExit.complete();
        });
    }

    /** Applies the various positioning and user-configured CSS classes to the alert. */
    private _afterPortalAttached() {
        const element: HTMLElement = this.elementRef.nativeElement;
        const alertClass = `vds-alert--${this.alertConfig.alertType}`;

        if (alertClass) {
            element.classList.add(alertClass);
        }

        this._exposeToModals();

        if (this.alertConfig.verticalPosition === "top") {
            element.classList.add("vds-alert--pos-top");
        } else if (this.alertConfig.verticalPosition === "bottom") {
            element.classList.add("vds-alert--pos-bottom");
        }
    }

    private _exposeToModals() {
        const id = this._liveElementId;
        const modals = this.document.querySelectorAll("body > .cdk-overlay-container [aria-modal='true']");

        for (let i = 0; i < modals.length; i++) {
            const modal = modals[i];
            const ariaOwns = modal.getAttribute("aria-owns");
            this._trackedModals.add(modal);

            if (!ariaOwns) {
                modal.setAttribute("aria-owns", id);
            } else if (ariaOwns.indexOf(id) === -1) {
                modal.setAttribute("aria-owns", ariaOwns + " " + id);
            }
        }
    }

    private _clearFromModals() {
        this._trackedModals.forEach((modal) => {
            const ariaOwns = modal.getAttribute("aria-owns");

            if (ariaOwns) {
                const newValue = ariaOwns.replace(this._liveElementId, "").trim();

                if (newValue.length > 0) {
                    modal.setAttribute("aria-owns", newValue);
                } else {
                    modal.removeAttribute("aria-owns");
                }
            }
        });

        this._trackedModals.clear();
    }

    /** Asserts that no content is already attached to the container. */
    private _assertNotAttached() {
        if (this._portalOutlet().hasAttached()) {
            throw Error("Attempting to attach alert content after content is already attached");
        }
    }

    private _screenReaderAnnounce() {
        if (this._announceTimeoutId) {
            return;
        }

        this.ngZone.runOutsideAngular(() => {
            this._announceTimeoutId = setTimeout(() => {
                if (this._destroyed) {
                    return;
                }

                const element = this.elementRef.nativeElement;
                const inertElement = element.querySelector("[aria-hidden]");
                const liveElement = element.querySelector("[aria-live]");

                if (inertElement && liveElement) {
                    let focusedElement: HTMLElement | null = null;
                    if (
                        this.platform.isBrowser &&
                        document.activeElement instanceof HTMLElement &&
                        inertElement.contains(document.activeElement)
                    ) {
                        focusedElement = document.activeElement;
                    }

                    inertElement.removeAttribute("aria-hidden");
                    liveElement.appendChild(inertElement);
                    focusedElement?.focus();

                    this._onAnnounce.next();
                    this._onAnnounce.complete();
                }
            }, this._announceDelay);
        });
    }
}
